import React, { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { nl, fr, enGB } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { createChild, updateChild } from '../services/api.shop';
import { useFrontendAuth } from '../hooks/useAuth';
import ShopCheckbox from './ShopCheckbox';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChildDto, CreateChildDto, Gender, UpdateChildDto } from '../types/index';
import { useTranslation } from '../context/TranslationContext';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import { ValidationError } from '../admin/components/ValidationError';
import ShopSelect from './ShopSelect';
import { parseLocalYmd, todayLocalYmd, toLocalYmd } from '../utils/localDate';

registerLocale('nl-be', nl);
registerLocale('nl', nl);
registerLocale('fr-be', fr);
registerLocale('fr', fr);
registerLocale('en-GB', enGB);

function pickerLocale(lang: string): string {
    const code = (lang || 'en').toLowerCase();
    if (code.startsWith('nl')) return 'nl-be';
    if (code.startsWith('fr')) return 'fr-be';
    return 'en-GB';
}

const NEWBORN_AVERAGES = {
    [Gender.Boy]: { heightCm: 50, weightGrams: 3482 },
    [Gender.Girl]: { heightCm: 49, weightGrams: 3374 },
    [Gender.PreferNotToSay]: { heightCm: 49.5, weightGrams: 3428 },
} as const;

interface ChildModalProps {
    selectedChild: ChildDto | null;
    initialIsExpected?: boolean;
    onSave: (savedChild: ChildDto) => void;
    onClose: () => void;
    onDelete: (id: number) => void;
}

type FieldErrors = Record<string, string>;

const ChildModal: React.FC<ChildModalProps> = ({ selectedChild, initialIsExpected, onSave, onClose, onDelete }) => {
    const [name, setName] = useState('');
    const [birthDate, setBirthDate] = useState<string | null>(null);
    const [expectedBirthDate, setExpectedBirthDate] = useState<string | null>(null);
    const [heightCm, setHeightCm] = useState<number | null>(null);
    const [weightGrams, setWeightGrams] = useState<number | null>(null);
    const [gender, setGender] = useState<Gender>(Gender.PreferNotToSay);
    const [isExpected, setIsExpected] = useState(false);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [isCreating, setIsCreating] = useState(false);
    const [childDataConsent, setChildDataConsent] = useState(false);
    const { user } = useFrontendAuth();
    const queryClient = useQueryClient();
    const needsConsent = !user?.childDataConsentAt;

    const today = new Date();
    const fiveYearsAgo = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate());
    const fiveYearsFuture = new Date(today.getFullYear() + 5, today.getMonth(), today.getDate());

    const { t, currentLang } = useTranslation();
    const tr = (key: string) => t(key);
    const dateLocale = pickerLocale(currentLang);
    const usePortalPicker = typeof window !== 'undefined' && window.innerWidth < 768;

    const fieldClass = (key: string) =>
        `w-full rounded-xl border p-2.5 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] bg-[var(--bg-color)] ${
            errors[key]
                ? 'border-red-300 bg-red-50'
                : 'border-[color-mix(in_srgb,var(--text-color),transparent_85%)]'
        }`;

    useEffect(() => {
        setErrors({});
        if (selectedChild) {
            setIsCreating(false);
            setName(selectedChild.name);
            {
                const born = parseLocalYmd(selectedChild.birthDate);
                setBirthDate(born ? toLocalYmd(born) : null);
                const expected = parseLocalYmd(selectedChild.expectedBirthDate);
                setExpectedBirthDate(expected ? toLocalYmd(expected) : null);
            }
            setHeightCm(selectedChild.heightCm || null);
            setWeightGrams(selectedChild.weightKg ? Math.round(selectedChild.weightKg * 1000) : null);
            setGender(selectedChild?.gender ?? Gender.PreferNotToSay);
            setIsExpected(!!selectedChild.expectedBirthDate);
        } else {
            setIsCreating(true);
            setIsExpected(initialIsExpected ?? false);
            setName('');
            setBirthDate(null);
            setExpectedBirthDate(null);
            setHeightCm(null);
            setWeightGrams(null);
            setGender(Gender.PreferNotToSay);
        }
    }, [selectedChild, initialIsExpected]);

    useEffect(() => {
        if (!isCreating || !isExpected) return;
        const avg = NEWBORN_AVERAGES[gender];
        setHeightCm(avg.heightCm);
        setWeightGrams(avg.weightGrams);
    }, [gender, isCreating, isExpected]);

    const clearFieldError = (key: string) => {
        setErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const next: FieldErrors = {};

        if (!name.trim()) {
            next.name = tr('form.childmodal.name.required');
        }

        if (heightCm !== null && heightCm < 25) {
            next.heightCm = tr('form.childmodal.height.min');
        }
        if (weightGrams !== null && weightGrams < 1000) {
            next.weightGrams = tr('form.childmodal.weight.min');
        }

        const todayStr = todayLocalYmd();
        if (!isExpected) {
            if (!birthDate) {
                next.birthDate = tr('form.childmodal.birthdate.required');
            } else if (birthDate > todayStr) {
                next.birthDate = tr('form.childmodal.birthdate.past');
            }
        } else {
            if (!expectedBirthDate) {
                next.expectedBirthDate = tr('form.childmodal.expectedbirthdate.required');
            } else if (expectedBirthDate < todayStr) {
                next.expectedBirthDate = tr('form.childmodal.expectedbirthdate.future');
            }
        }

        if (!selectedChild && needsConsent && !childDataConsent) {
            next.childDataConsent = tr('form.childmodal.consent.required');
        }

        if (Object.keys(next).length > 0) {
            setErrors(next);
            scrollToFirstErrorField(next);
            return;
        }

        setErrors({});

        const dto: CreateChildDto | UpdateChildDto = {
            name: name.trim(),
            birthDate: isExpected ? null : birthDate,
            expectedBirthDate: isExpected ? expectedBirthDate : null,
            heightCm,
            weightKg: weightGrams ? weightGrams / 1000 : null,
            gender,
            ...(!selectedChild && needsConsent ? { childDataConsent: true } : {}),
        };

        try {
            let savedChild: ChildDto;
            if (selectedChild) {
                const res = await updateChild(selectedChild.id, dto as UpdateChildDto);
                savedChild = res.data;
            } else {
                const res = await createChild(dto);
                savedChild = res.data;
            }
            if (!selectedChild && needsConsent) {
                await queryClient.invalidateQueries({ queryKey: ['user'] });
            }
            onSave(savedChild);
            onClose();
        } catch (err) {
            console.error(err);
            const fail: FieldErrors = { General: tr('form.childmodal.errors.savefailed') };
            setErrors(fail);
            scrollToFirstErrorField(fail);
        }
    };

    const tabBtn = (expected: boolean, label: string, emoji: string) => {
        const active = isExpected === expected;
        return (
            <button
                type="button"
                onClick={() => {
                    if (!selectedChild) {
                        setIsExpected(expected);
                        setErrors((prev) => {
                            const n = { ...prev };
                            delete n.birthDate;
                            delete n.expectedBirthDate;
                            return n;
                        });
                    }
                }}
                disabled={!!selectedChild}
                className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-medium transition border ${
                    active
                        ? 'border-transparent text-[var(--text-color)] shadow-sm'
                        : 'border-[color-mix(in_srgb,var(--text-color),transparent_85%)] bg-transparent text-[var(--text-color)] opacity-80'
                } disabled:opacity-60`}
                style={active ? { backgroundColor: 'var(--accent-color)' } : undefined}
            >
                <span className="mr-1" aria-hidden>
                    {emoji}
                </span>
                {label}
            </button>
        );
    };

    return (
        <form onSubmit={handleSubmit} className="text-[var(--text-color)]" noValidate>
            <div className="mb-4 flex items-start justify-between gap-2">
                <h2 className="text-xl font-bold">
                    {selectedChild ? t('form.childmodal.title.edit') : tr('form.childmodal.title.addbaby')}
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-xl leading-none opacity-50 hover:opacity-100"
                    aria-label={t('common.cancel')}
                >
                    ×
                </button>
            </div>

            {!selectedChild && (
                <div className="mb-4 flex gap-2">
                    {tabBtn(false, tr('form.childmodal.tab.born'), '👶')}
                    {tabBtn(true, tr('form.childmodal.tab.ontheway'), '🤰')}
                </div>
            )}

            <ValidationError error={errors.General} />

            <div className="mb-4">
                <label className="mb-1 block text-sm font-medium opacity-80" htmlFor="child-name">
                    {t('form.childmodal.name.label')}
                </label>
                <input
                    id="child-name"
                    name="name"
                    data-error-field="name"
                    type="text"
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        clearFieldError('name');
                    }}
                    className={fieldClass('name')}
                />
                <ValidationError error={errors.name} />
            </div>

            <div className="mb-4" data-error-field="gender">
                <label className="mb-1 block text-sm font-medium opacity-80" htmlFor="child-gender">
                    {t('form.childmodal.gender.label')}
                </label>
                <ShopSelect
                    id="child-gender"
                    value={gender}
                    onChange={(v) => {
                        setGender(v as Gender);
                        clearFieldError('gender');
                    }}
                    options={[
                        { value: Gender.Boy, label: t('form.childmodal.gender.boy') },
                        { value: Gender.Girl, label: t('form.childmodal.gender.girl') },
                        {
                            value: Gender.PreferNotToSay,
                            label: t('form.childmodal.gender.prefernottosay'),
                        },
                    ]}
                    ariaLabel={t('form.childmodal.gender.label')}
                />
                <ValidationError error={errors.gender} />
            </div>

            {!isExpected ? (
                <div className="mb-4" data-error-field="birthDate" data-field="birthDate">
                    <label className="mb-1 block text-sm font-medium opacity-80" htmlFor="child-birthDate">
                        {t('form.childmodal.birthdate.label')}
                    </label>
                    <DatePicker
                        id="child-birthDate"
                        name="birthDate"
                        selected={parseLocalYmd(birthDate)}
                        onChange={(date: Date | null) => {
                            setBirthDate(date ? toLocalYmd(date) : null);
                            clearFieldError('birthDate');
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/yyyy"
                        locale={dateLocale}
                        calendarStartDay={1}
                        withPortal={usePortalPicker}
                        className={fieldClass('birthDate')}
                        wrapperClassName="w-full"
                        minDate={fiveYearsAgo}
                        maxDate={today}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                    <ValidationError error={errors.birthDate} />
                </div>
            ) : (
                <div className="mb-4" data-error-field="expectedBirthDate" data-field="expectedBirthDate">
                    <label className="mb-1 block text-sm font-medium opacity-80" htmlFor="child-expectedBirthDate">
                        {t('form.childmodal.expectedbirthdate.label')}
                    </label>
                    <DatePicker
                        id="child-expectedBirthDate"
                        name="expectedBirthDate"
                        selected={parseLocalYmd(expectedBirthDate)}
                        onChange={(date: Date | null) => {
                            setExpectedBirthDate(date ? toLocalYmd(date) : null);
                            clearFieldError('expectedBirthDate');
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/yyyy"
                        locale={dateLocale}
                        calendarStartDay={1}
                        withPortal={usePortalPicker}
                        className={fieldClass('expectedBirthDate')}
                        wrapperClassName="w-full"
                        minDate={today}
                        maxDate={fiveYearsFuture}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                    <ValidationError error={errors.expectedBirthDate} />
                </div>
            )}

            <div className="mb-4">
                <label className="mb-1 block text-sm font-medium opacity-80" htmlFor="child-heightCm">
                    {t('form.childmodal.height.label')}
                </label>
                <input
                    id="child-heightCm"
                    name="heightCm"
                    data-error-field="heightCm"
                    type="number"
                    min="25"
                    step="0.1"
                    value={heightCm ?? ''}
                    onChange={(e) => {
                        setHeightCm(e.target.value ? parseFloat(e.target.value) : null);
                        clearFieldError('heightCm');
                    }}
                    className={fieldClass('heightCm')}
                />
                <ValidationError error={errors.heightCm} />
            </div>

            <div className="mb-4">
                <label className="mb-1 block text-sm font-medium opacity-80" htmlFor="child-weightGrams">
                    {t('form.childmodal.weight.label')}
                </label>
                <input
                    id="child-weightGrams"
                    name="weightGrams"
                    data-error-field="weightGrams"
                    type="number"
                    min="1000"
                    step="1"
                    value={weightGrams ?? ''}
                    onChange={(e) => {
                        setWeightGrams(e.target.value ? parseInt(e.target.value, 10) : null);
                        clearFieldError('weightGrams');
                    }}
                    className={fieldClass('weightGrams')}
                />
                <p className="mt-1 text-xs opacity-50">{t('form.childmodal.weight.example')}</p>
                <ValidationError error={errors.weightGrams} />
            </div>

            {!selectedChild && needsConsent && (
                <div className="mt-4 space-y-2" data-error-field="childDataConsent">
                    <div className="flex items-start gap-3 text-sm">
                        <ShopCheckbox
                            id="child-data-consent"
                            checked={childDataConsent}
                            onChange={(v) => {
                                setChildDataConsent(v);
                                clearFieldError('childDataConsent');
                            }}
                        />
                        <label htmlFor="child-data-consent" className="leading-snug">
                            {t('form.childmodal.consent.label') === 'form.childmodal.consent.label'
                                ? 'I am the parent or guardian. Petito may use this to personalise sizes and checklists. I can delete it anytime.'
                                : t('form.childmodal.consent.label')}{' '}
                            <Link to="/privacy#children" className="font-semibold text-[var(--accent-color)] underline">
                                {t('page.privacypolicy.title') === 'page.privacypolicy.title'
                                    ? 'Privacy Policy'
                                    : t('page.privacypolicy.title')}
                            </Link>
                        </label>
                    </div>
                    <ValidationError error={errors.childDataConsent} />
                </div>
            )}

            {selectedChild ? (
                <div className="mt-6 flex justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => onDelete(selectedChild!.id)}
                        className="rounded-xl bg-[color-mix(in_srgb,var(--text-color),transparent_90%)] px-4 py-3 text-[var(--text-color)] hover:opacity-90"
                    >
                        {t('common.deleteprofile')}
                    </button>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl bg-[color-mix(in_srgb,var(--text-color),transparent_90%)] px-4 py-3"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="rounded-xl bg-[var(--accent-color)] px-6 py-3 font-semibold text-white shadow-md hover:opacity-90"
                        >
                            {t('common.save')}
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="submit"
                    className="mt-2 w-full rounded-full bg-[var(--accent-color)] py-3 text-center font-semibold text-white shadow-md hover:opacity-90"
                >
                    {tr('form.childmodal.submit.journey')}
                </button>
            )}
        </form>
    );
};

export default ChildModal;
